import { useState, useEffect, useRef, useCallback } from 'react';
import { SignInButton, UserButton, useAuth, useUser } from '@clerk/clerk-react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ThemeSwitcher } from '@features/theme-switcher';
import { cmsQueries } from '@entities/cms';
import type { CmsNavItemTree } from '@fanini/shared-types';

/**
 * Fallback-Navigation — wird angezeigt solange die CMS-Navigation lädt oder bei Fehler.
 * Entspricht der vorherigen hardcodierten Navigation.
 */
const NOW = new Date();

const FALLBACK_NAV: CmsNavItemTree[] = [
  {
    id: 'fallback-1',
    label: 'Startseite',
    url: '/',
    type: 'link',
    openInNewTab: false,
    sortOrder: 0,
    parentId: null,
    isVisible: true,
    createdAt: NOW,
    updatedAt: NOW,
    children: [],
  },
  {
    id: 'fallback-2',
    label: 'Tickets',
    url: '/tickets',
    type: 'link',
    openInNewTab: false,
    sortOrder: 1,
    parentId: null,
    isVisible: true,
    createdAt: NOW,
    updatedAt: NOW,
    children: [],
  },
  {
    id: 'fallback-3',
    label: 'Kalender',
    url: '/kalender',
    type: 'link',
    openInNewTab: false,
    sortOrder: 2,
    parentId: null,
    isVisible: true,
    createdAt: NOW,
    updatedAt: NOW,
    children: [],
  },
  {
    id: 'fallback-4',
    label: 'Über uns',
    url: '/uber-uns',
    type: 'link',
    openInNewTab: false,
    sortOrder: 3,
    parentId: null,
    isVisible: true,
    createdAt: NOW,
    updatedAt: NOW,
    children: [],
  },
];

interface NavLinkProps {
  item: CmsNavItemTree;
  onClick?: () => void;
  isMobile?: boolean;
}

/**
 * Einzelner Navigations-Link oder Dropdown-Gruppe.
 * Unterstützt interne TanStack-Router-Links, externe Links und Dropdown-Gruppen.
 */
function NavItem({ item, onClick, isMobile = false }: NavLinkProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  // Dropdown schließen bei Klick außerhalb
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const href = item.url ?? (item.pageSlug ? `/${item.pageSlug}` : '#');
  const isExternal = item.type === 'external';
  const isGroup = item.type === 'group' && (item.children?.length ?? 0) > 0;

  const linkClasses = isMobile
    ? 'block px-3 py-2.5 text-sm font-black uppercase tracking-wide text-white/60 hover:text-white hover:bg-white/5 transition-colors duration-150 no-underline'
    : 'header-nav-link';

  if (isGroup) {
    return (
      <li ref={dropdownRef} className={isMobile ? '' : 'relative'}>
        <button
          type="button"
          className={
            isMobile
              ? `${linkClasses} flex w-full items-center justify-between`
              : `${linkClasses} flex items-center gap-1 cursor-pointer bg-transparent border-none`
          }
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          onClick={() => setDropdownOpen((v) => !v)}
          onMouseEnter={isMobile ? undefined : () => setDropdownOpen(true)}
          onMouseLeave={isMobile ? undefined : () => setDropdownOpen(false)}
        >
          {item.label}
          <svg
            className={`h-3 w-3 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {dropdownOpen && (
          <ul
            className={
              isMobile
                ? 'mt-1 space-y-0 list-none pl-4'
                : 'nav-dropdown absolute top-full left-0 z-50 min-w-max list-none py-2 shadow-xl'
            }
            role="menu"
            onMouseEnter={isMobile ? undefined : () => setDropdownOpen(true)}
            onMouseLeave={isMobile ? undefined : () => setDropdownOpen(false)}
          >
            {(item.children ?? []).map((child) => {
              const childHref = child.url ?? (child.pageSlug ? `/${child.pageSlug}` : '#');
              const childIsExternal = child.type === 'external';

              return (
                <li key={child.id} role="menuitem">
                  {childIsExternal ? (
                    <a
                      href={childHref}
                      target={child.openInNewTab ? '_blank' : undefined}
                      rel={child.openInNewTab ? 'noopener noreferrer' : undefined}
                      className={
                        isMobile
                          ? 'block px-3 py-2 text-sm font-bold text-white/50 hover:text-white transition-colors duration-150 no-underline'
                          : 'nav-dropdown-item'
                      }
                      onClick={onClick}
                    >
                      {child.label}
                    </a>
                  ) : (
                    <Link
                      to={childHref as '/'}
                      className={
                        isMobile
                          ? 'block px-3 py-2 text-sm font-bold text-white/50 hover:text-white transition-colors duration-150 no-underline'
                          : 'nav-dropdown-item'
                      }
                      onClick={onClick}
                    >
                      {child.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li>
      {isExternal ? (
        <a
          href={href}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
          className={linkClasses}
          onClick={onClick}
        >
          {item.label}
        </a>
      ) : (
        <Link to={href as '/'} className={linkClasses} onClick={onClick}>
          {item.label}
        </Link>
      )}
    </li>
  );
}

/**
 * Globaler Header der Anwendung.
 * Dynamische Navigation aus CMS mit Dropdown-Menüs für Gruppen.
 * Fallback auf hardcodierte Links bei Lade- oder Fehler-Zustand.
 * Dramatischer Ultra-Stil: sticky, Backdrop-Blur, Fan-Logo, Akzent-Stripe.
 *
 * @example
 * ```tsx
 * <Header />
 * ```
 */
export function Header() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [wordmarkFailed, setWordmarkFailed] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const role = user?.publicMetadata?.role as string | undefined;
  const isDashboardUser = role === 'admin' || role === 'team';

  const {
    data: navData,
    isLoading: navLoading,
    isError: navError,
  } = useQuery(cmsQueries.navigation());

  // Fallback wenn API lädt oder Fehler hat
  const navItems: CmsNavItemTree[] =
    navLoading || navError || !navData?.items?.length
      ? FALLBACK_NAV
      : navData.items.sort((a, b) => a.sortOrder - b.sortOrder);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Body-Scroll sperren wenn Menü offen ist
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Klick außerhalb schließt das Menü
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target as Node)
      ) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, closeMenu]);

  return (
    <>
      <style>{`
        .header-root {
          position: sticky;
          top: 0;
          z-index: 50;
          background-color: rgba(29, 29, 27, 0.97);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition: box-shadow 0.3s ease, background-color 0.3s ease;
        }

        .header-root.is-scrolled {
          box-shadow: 0 2px 24px rgba(0, 0, 0, 0.6);
          background-color: rgba(29, 29, 27, 0.99);
        }

        .header-accent-stripe {
          height: 3px;
          background: linear-gradient(90deg, #b84e48 0%, #c96b66 50%, #b84e48 100%);
          background-size: 200% 100%;
          animation: stripeShift 6s ease infinite;
        }

        @keyframes stripeShift {
          0% { background-position: 0% 0%; }
          50% { background-position: 100% 0%; }
          100% { background-position: 0% 0%; }
        }

        .header-logo-img {
          height: 2rem;
          width: auto;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }

        .header-logo-link:hover .header-logo-img {
          opacity: 0.85;
          transform: scale(1.04);
        }

        .header-nav-link {
          position: relative;
          padding: 0.5rem 1rem;
          font-size: 0.75rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(255, 255, 255, 0.55);
          text-decoration: none;
          transition: color 0.15s ease;
          background: transparent;
          border: none;
          cursor: pointer;
        }

        .header-nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 1rem;
          right: 1rem;
          height: 2px;
          background-color: #b84e48;
          transform: scaleX(0);
          transform-origin: center;
          transition: transform 0.25s ease;
        }

        .header-nav-link:hover,
        .header-nav-link[data-status="active"] {
          color: #ffffff;
        }

        .header-nav-link:hover::after,
        .header-nav-link[data-status="active"]::after {
          transform: scaleX(1);
        }

        /* Dropdown-Menü */
        .nav-dropdown {
          background-color: rgba(22, 22, 20, 0.98);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-top: 2px solid #b84e48;
          backdrop-filter: blur(12px);
          animation: dropdownIn 0.15s ease-out forwards;
        }

        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .nav-dropdown-item {
          display: block;
          padding: 0.6rem 1.25rem;
          font-size: 0.7rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: rgba(255, 255, 255, 0.5);
          text-decoration: none;
          transition: color 0.15s ease, background-color 0.15s ease, padding-left 0.2s ease;
          white-space: nowrap;
        }

        .nav-dropdown-item:hover {
          color: #ffffff;
          background-color: rgba(184, 78, 72, 0.1);
          padding-left: 1.5rem;
        }

        .mobile-menu-enter {
          animation: mobileMenuIn 0.2s ease-out forwards;
        }

        @keyframes mobileMenuIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Overlay-Hintergrund hinter dem Mobilmenü */
        .mobile-menu-overlay {
          position: fixed;
          inset: 0;
          z-index: 40;
          background-color: rgba(0, 0, 0, 0.5);
          animation: overlayFadeIn 0.2s ease-out forwards;
        }

        @keyframes overlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Abdunkelungs-Overlay hinter dem Mobilmenü */}
      {menuOpen && (
        <div className="mobile-menu-overlay md:hidden" aria-hidden="true" onClick={closeMenu} />
      )}

      <header className={`header-root${scrolled ? ' is-scrolled' : ''}`}>
        {/* Animierter Akzent-Stripe */}
        <div className="header-accent-stripe" aria-hidden="true" />

        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/"
            className="header-logo-link flex items-center gap-3 no-underline"
            aria-label="Faninitiative Spandau — Startseite"
          >
            <img
              src="/logos/fan-logo-white.svg"
              alt=""
              aria-hidden="true"
              className="header-logo-img"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            {wordmarkFailed ? (
              <span className="font-black uppercase tracking-wide text-white text-sm">
                FANINITIATIVE
              </span>
            ) : (
              <img
                src="/logos/fan-wordmark-white.svg"
                alt="Faninitiative Spandau"
                className="h-5 w-auto hidden sm:block"
                onError={() => setWordmarkFailed(true)}
              />
            )}
            {!wordmarkFailed && (
              <span className="sm:hidden font-black uppercase tracking-wide text-white text-sm">
                FANINITIATIVE
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Hauptnavigation" className="hidden md:block">
            <ul className="flex list-none items-center gap-0">
              {navItems.map((item) => (
                <NavItem key={item.id} item={item} />
              ))}
            </ul>
          </nav>

          {/* Auth + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <ThemeSwitcher />
            {isSignedIn ? (
              <div className="flex items-center gap-2">
                {isDashboardUser && (
                  <a
                    href="https://dashboard.fanini.live"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:inline-block px-3 py-1.5 text-xs font-black uppercase tracking-wide border border-white/20 text-white/60 hover:border-accent hover:text-accent transition-colors duration-150 no-underline"
                  >
                    Dashboard
                  </a>
                )}
                <a
                  href="/profil"
                  className="hidden sm:inline-block px-3 py-1.5 text-xs font-black uppercase tracking-wide text-white/50 hover:text-white transition-colors duration-150 no-underline"
                >
                  Profil
                </a>
                <UserButton afterSignOutUrl="/" />
              </div>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/">
                <button
                  type="button"
                  className="hidden sm:inline-block px-4 py-2 text-xs font-black uppercase tracking-wide bg-accent text-white hover:bg-accent/80 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-ink"
                >
                  Anmelden
                </button>
              </SignInButton>
            )}

            {/* Mobile Hamburger */}
            <button
              ref={hamburgerRef}
              type="button"
              className="md:hidden flex flex-col gap-1.5 p-2 text-white/60 hover:text-white transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-ink"
              aria-label={menuOpen ? 'Navigation schließen' : 'Navigation öffnen'}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span
                className={`block h-0.5 w-5 bg-current transition-transform duration-200 ${menuOpen ? 'translate-y-2 rotate-45' : ''}`}
              />
              <span
                className={`block h-0.5 w-5 bg-current transition-opacity duration-200 ${menuOpen ? 'opacity-0' : ''}`}
              />
              <span
                className={`block h-0.5 w-5 bg-current transition-transform duration-200 ${menuOpen ? '-translate-y-2 -rotate-45' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <nav
            ref={menuRef}
            id="mobile-nav"
            aria-label="Mobile Navigation"
            className="mobile-menu-enter border-t border-white/10 px-4 py-4"
            style={{ backgroundColor: 'rgba(29, 29, 27, 0.99)' }}
          >
            <ul className="flex flex-col list-none gap-0.5">
              {navItems.map((item) => (
                <NavItem key={item.id} item={item} isMobile onClick={() => setMenuOpen(false)} />
              ))}
              {isSignedIn && (
                <li>
                  <a
                    href="/profil"
                    onClick={closeMenu}
                    className="block px-3 py-2.5 text-sm font-black uppercase tracking-wide text-accent hover:text-white transition-colors duration-150 no-underline"
                  >
                    Mein Profil
                  </a>
                </li>
              )}
              {isSignedIn && isDashboardUser && (
                <li>
                  <a
                    href="https://dashboard.fanini.live"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                    className="block px-3 py-2.5 text-sm font-black uppercase tracking-wide text-accent hover:text-white transition-colors duration-150 no-underline"
                  >
                    Dashboard &rarr;
                  </a>
                </li>
              )}
              {!isSignedIn && (
                <li className="pt-2 border-t border-white/10 mt-2">
                  <SignInButton mode="modal" forceRedirectUrl="/">
                    <button
                      type="button"
                      onClick={closeMenu}
                      className="w-full px-3 py-2.5 text-sm font-black uppercase tracking-wide bg-accent text-white hover:bg-accent/80 transition-colors duration-150 text-left focus:outline-none"
                    >
                      Anmelden
                    </button>
                  </SignInButton>
                </li>
              )}
            </ul>
          </nav>
        )}
      </header>
    </>
  );
}
